/**
 * Axios instance for the Cracked Mug API.
 *
 * Two interceptors:
 *   - Request: attach the current user's Firebase ID token.
 *   - Response:
 *       - On 401, force exactly one token refresh and retry the request once;
 *         if the refreshed token is still rejected (revoked session, role
 *         downgrade, disabled account), sign the user out so they're cleanly
 *         bounced to re-auth instead of being stranded behind protected UI
 *         that 401s on every call until a manual reload.
 *       - On 4xx/5xx with a structured `{success:false, error}` body, re-throw
 *         with the API's human-readable message instead of axios's generic
 *         `Request failed with status code 400`. Without this, every
 *         application error (SHOP_CLOSED, VALIDATION_ERROR, etc.) would surface
 *         to the user as the same opaque axios string.
 */
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@cracked-mug/shared';
import { auth } from '../firebase';
import { useAuthStore } from '../store/authStore';

export const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 10_000,
});

api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

/**
 * A request the response interceptor has already retried once after forcing a
 * token refresh. The flag rides on the axios config (it survives `mergeConfig`
 * on the retry, since unknown config keys are copied through), so a retry that
 * *also* 401s can't recurse into another refresh — it falls through to
 * sign-out instead. This is the guard against an infinite refresh/retry loop.
 */
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

api.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (error instanceof AxiosError) {
      // A 401 reaching this point is NOT routine expiry: the request
      // interceptor's `getIdToken()` already silently refreshes a merely-expired
      // token, so the server is rejecting a token Firebase still considers valid
      // — a revoked session, a role downgrade, or a disabled account. Force
      // exactly one hard refresh and retry once; only if the brand-new token is
      // ALSO rejected do we treat the credential as dead. This avoids signing
      // anyone out over a single transient 401 before the refresh-retry.
      const original = error.config as RetriableConfig | undefined;
      if (error.response?.status === 401 && original) {
        if (!original._retried) {
          original._retried = true;
          try {
            const freshToken = await auth.currentUser?.getIdToken(true);
            if (freshToken) {
              original.headers.set('Authorization', `Bearer ${freshToken}`);
              return api(original);
            }
          } catch {
            // The forced refresh itself failed (e.g. a disabled account, which
            // makes getIdToken reject) — the credential can't be salvaged, so
            // fall through to sign-out.
          }
        }
        // Reached when the retry came back 401 again, when there was no
        // signed-in user, or when the forced refresh threw. Sign out: this
        // clears the cart and resets the auth store via the onAuthStateChanged
        // listener, dropping the user back to a clean re-auth.
        await useAuthStore.getState().signOut();
      }

      const body = error.response?.data as Partial<ApiError> | undefined;
      if (body?.success === false && typeof body.error === 'string') {
        const friendly = new Error(body.error);
        // Stash the API code on `.name` so callers that want to discriminate
        // (e.g. retry on RATE_LIMITED) can do so without parsing the message.
        friendly.name = body.code ?? 'ApiError';
        return Promise.reject(friendly);
      }
    }
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  },
);
